/**
 * Toast — minimal, dependency-free notification surface.
 *
 * Emit from anywhere (including non-React code like the TanStack Query
 * MutationCache) via the module-level `toast` singleton:
 *
 *   toast.error('Could not save changes');
 *   toast.success('Finding accepted');
 *
 * Mount <Toaster /> once at the app root. It subscribes to the singleton
 * and renders an aria-live stack. Errors use role="alert" (assertive);
 * success/info use role="status" (polite).
 *
 * ponytail: hand-rolled instead of @radix-ui/react-toast — this is ~60
 * lines and needs no new dependency. Add Radix only if we later need
 * swipe-to-dismiss, action buttons, or a pause-on-hover queue.
 */

import { useEffect, useState } from 'react';
import { X, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastKind = 'success' | 'error' | 'info';
export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

type Listener = (t: ToastItem) => void;
let listeners: Listener[] = [];
let counter = 0;

function emit(kind: ToastKind, message: string) {
  const item: ToastItem = { id: ++counter, kind, message };
  listeners.forEach((l) => l(item));
}

export const toast = {
  success: (message: string) => emit('success', message),
  error: (message: string) => emit('error', message),
  info: (message: string) => emit('info', message),
};

const AUTO_DISMISS_MS = 5000;

const KIND_STYLES: Record<ToastKind, { icon: typeof Info; ring: string; iconColor: string }> = {
  success: { icon: CheckCircle2, ring: 'border-emerald-600/40', iconColor: 'text-emerald-600' },
  error: { icon: AlertTriangle, ring: 'border-error/40', iconColor: 'text-error' },
  info: { icon: Info, ring: 'border-outline-variant', iconColor: 'text-primary' },
};

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    const l: Listener = (t) => setItems((prev) => [...prev, t]);
    listeners.push(l);
    return () => {
      listeners = listeners.filter((x) => x !== l);
    };
  }, []);

  const dismiss = (id: number) => setItems((prev) => prev.filter((t) => t.id !== id));

  return (
    <div
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-[min(92vw,22rem)] pointer-events-none"
      role="region"
      aria-label="Notifications"
    >
      {items.map((t) => (
        <ToastRow key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastRow({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const { icon: Icon, ring, iconColor } = KIND_STYLES[item.kind];

  useEffect(() => {
    const timer = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role={item.kind === 'error' ? 'alert' : 'status'}
      aria-live={item.kind === 'error' ? 'assertive' : 'polite'}
      className={cn(
        'pointer-events-auto flex items-start gap-2.5 rounded-xl border bg-surface-container-lowest px-3.5 py-3 shadow-[0_8px_24px_-10px_rgb(25_28_29_/_0.22)]',
        ring,
      )}
    >
      <Icon className={cn('w-4 h-4 mt-0.5 shrink-0', iconColor)} aria-hidden />
      <p className="flex-1 text-caption text-on-surface leading-snug">{item.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="shrink-0 rounded p-0.5 text-on-surface-variant hover:text-on-surface hover:bg-surface-variant/60 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
